import axiosInstance from '@/Api/axiosV2Config';
import type {
  InvoiceListFilters,
  InvoiceListResponse,
  RegeneratePdfPreview,
  RegeneratePdfResult,
} from '../types/invoice.types';

function triggerDownload(blob: Blob, filename: string) {
  const url = window.URL.createObjectURL(blob);
  const link = document.createElement('a');
  link.href = url;
  link.setAttribute('download', filename);
  document.body.appendChild(link);
  link.click();
  link.remove();
  window.URL.revokeObjectURL(url);
}

function extractFilename(response: any, fallback: string): string {
  const disposition = response.headers?.['content-disposition'] || '';
  const match = disposition.match(/filename="?([^";\n]+)"?/);
  return match?.[1] || fallback;
}

export const invoiceApi = {
  list: async (filters?: InvoiceListFilters): Promise<InvoiceListResponse> => {
    const { societyId, ...params } = filters || {};
    if (societyId) {
      // Admin endpoint: retourne un tableau brut, pas de pagination côté serveur
      const { data } = await axiosInstance.get<any[]>(`/admin/vendors/${societyId}/invoices`);
      return { data: data || [], total: data?.length || 0 };
    }
    const { data } = await axiosInstance.get<InvoiceListResponse>('/pro-v2/invoices', { params });
    return data;
  },

  downloadPdf: async (invoiceId: number, label?: string): Promise<void> => {
    const response = await axiosInstance.get(`/pro-v2/invoices/${invoiceId}/download`, {
      responseType: 'blob',
    });
    const filename = extractFilename(response, `Facture_${label || invoiceId}.pdf`);
    triggerDownload(new Blob([response.data]), filename);
  },

  downloadDetail: async (invoiceId: number, label?: string): Promise<void> => {
    const response = await axiosInstance.get(`/pro-v2/invoices/${invoiceId}/download-detail`, {
      responseType: 'blob',
      // Cache-buster : URL unique à chaque clic → contourne toute réponse en
      // cache navigateur (le type du fichier peut changer : CSV ↔ XLSX).
      params: { _t: Date.now() },
    });
    const filename = extractFilename(response, `detail_facture_${label || invoiceId}.csv`);
    triggerDownload(new Blob([response.data]), filename);
  },

  downloadFeesDetail: async (stripeAccountId: string, invoiceId: number, label?: string): Promise<void> => {
    // includeExported=true : nécessaire car le cron d'envoi du mail marque les
    // fees `csvExported=true` ; sans ce flag, le rejoue UI renverrait du vide.
    const response = await axiosInstance.post('/stripe/generate-reconciliation-csv', {
      stripeAccountId,
      invoiceId: String(invoiceId),
      // Détail d'une facture précise : inclure TOUTES ses lignes, même déjà
      // exportées (sinon vide/404 pour les factures dont les frais ont déjà
      // été exportés par le batch mensuel).
      includeExported: true,
    }, {
      responseType: 'blob',
    });
    const filename = extractFilename(response, `rapprochement_facture_${label || invoiceId}.csv`);
    triggerDownload(new Blob([response.data]), filename);
  },

  getRegeneratePdfPreview: async (invoiceId: number): Promise<RegeneratePdfPreview> => {
    const { data } = await axiosInstance.get<RegeneratePdfPreview>(
      `/billing/invoices/${invoiceId}/regenerate-pdf-preview`,
    );
    return data;
  },

  regenerateInvoicePdf: async (invoiceId: number): Promise<RegeneratePdfResult> => {
    const { data } = await axiosInstance.post<RegeneratePdfResult>(
      `/billing/invoices/${invoiceId}/regenerate-pdf`,
    );
    return data;
  },
};
